import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ActivityLogEntry } from "../../context/NotificationContext";
import { HiOutlinePlay, HiOutlineCheck, HiOutlineClock } from "react-icons/hi2";

interface ActivityLogProps {
  activities: ActivityLogEntry[];
  maxItems?: number;
}

export const ActivityLog: React.FC<ActivityLogProps> = ({
  activities,
  maxItems = 10,
}) => {
  const navigate = useNavigate();

  // Memoize displayed activities to prevent unnecessary recalculations
  const displayedActivities = useMemo(
    () => activities.slice(0, maxItems),
    [activities, maxItems],
  );

  const getIcon = (type: ActivityLogEntry["type"]) => {
    switch (type) {
      case "order_started":
        return <HiOutlinePlay className="w-4 h-4 text-brand" />;
      case "order_completed":
        return <HiOutlineCheck className="w-4 h-4 text-success" />;
      default:
        return <HiOutlineClock className="w-4 h-4 text-muted" />;
    }
  };

  const getIconBackgroundColor = (type: ActivityLogEntry["type"]) => {
    switch (type) {
      case "order_started":
        return "bg-brand-muted border-brand-muted";
      case "order_completed":
        return "bg-success-muted border-lgb-green-200";
      default:
        return "bg-surface-muted border-border-subtle";
    }
  };

  const formatTime = (timestamp: Date) => {
    return timestamp.toLocaleTimeString("no-NO", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatSubtitle = (activity: ActivityLogEntry) => {
    return activity.timestamp.toLocaleDateString("no-NO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const handleActivityClick = (activity: ActivityLogEntry) => {
    // Navigate to the specific order for all activity types
    navigate(`/orders?id=${activity.orderId}`);
  };

  if (displayedActivities.length === 0) {
    return (
      <div className="text-center py-8 text-muted">
        <HiOutlineClock className="w-8 h-8 mx-auto mb-2 text-muted" />
        <p>Ingen aktivitet i dag</p>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {displayedActivities.map((activity, index) => (
        <div
          key={activity.id}
          onClick={() => handleActivityClick(activity)}
          className="relative cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors duration-200 group"
        >
          {/* Desktop layout */}
          <div className="hidden sm:flex items-center py-3">
            {/* Timeline line */}
            {index < displayedActivities.length - 1 && (
              <div className="absolute left-6 top-12 bottom-0 w-px bg-surface-muted" />
            )}

            {/* Icon */}
            <div
              className={`flex-shrink-0 w-7 h-7 rounded-full border flex items-center justify-center relative z-10 shadow-sm ${getIconBackgroundColor(activity.type)}`}
            >
              {getIcon(activity.type)}
            </div>

            {/* Time */}
            <div className="ml-4 flex-shrink-0 min-w-[3rem]">
              <span className="text-sm font-medium text-text">
                {formatTime(activity.timestamp)}
              </span>
            </div>

            {/* Content */}
            <div className="ml-4 flex-1 min-w-0">
              <p className="text-sm font-medium text-text group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                {activity.message}
              </p>
              <p className="text-xs text-muted mt-0.5">
                {formatSubtitle(activity)}
              </p>
            </div>
          </div>

          {/* Mobile layout */}
          <div className="sm:hidden py-3 px-2">
            {/* Timeline line for mobile */}
            {index < displayedActivities.length - 1 && (
              <div className="absolute left-6 top-16 bottom-0 w-px bg-surface-muted" />
            )}

            {/* Top row: Icon and Time */}
            <div className="flex items-center mb-2">
              <div
                className={`flex-shrink-0 w-7 h-7 rounded-full border flex items-center justify-center relative z-10 shadow-sm ${getIconBackgroundColor(activity.type)}`}
              >
                {getIcon(activity.type)}
              </div>
              <div className="ml-3">
                <span className="text-sm font-medium text-text">
                  {formatTime(activity.timestamp)}
                </span>
              </div>
            </div>

            {/* Content row */}
            <div className="ml-10">
              <p className="text-sm font-medium text-text group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                {activity.message}
              </p>
              <p className="text-xs text-muted mt-0.5">
                {formatSubtitle(activity)}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
